import { z } from 'zod'
import * as Sentry from '@sentry/react'
import type { DesignApi } from '../../infrastructure/api/designApi'
import type { CanvasElement } from '../../domain/canvas/entities/element'
import { errorMessages } from '../shared/errorMessages'
import { ensureSvgAssetUrl } from '../../infrastructure/api/assetsApi'

/**
 * Load Design Use Case
 * Handles loading existing designs with proper error handling
 */

// Query schema for loading a design
export const LoadDesignQuerySchema = z.object({
  designId: z.string().min(1),
})

export type LoadDesignQuery = z.infer<typeof LoadDesignQuerySchema>

// Result type
export type LoadDesignResult = {
  success: boolean
  design?: {
    designId: string
    productId: number
    cartId: string
    background: string
    zones: {
      background: { width: number }
      export: { top: number; left: number; width: number; height: number }
      design: { top: number; left: number; width: number; height: number }
    }
  }
  elements?: Record<
    string,
    CanvasElement & { position: { x: number; y: number }; rotation?: number }
  >
  error?: string
}

// Factory function to create the use case
export const createLoadDesignUseCase = (designApi: DesignApi) => ({
  execute: async (query: LoadDesignQuery): Promise<LoadDesignResult> => {
    try {
      // Validate query
      const validatedQuery = LoadDesignQuerySchema.parse(query)

      // Load design from API
      const design = await designApi.loadDesign(validatedQuery.designId)

      // Transform elements if they exist
      const elements: LoadDesignResult['elements'] = {}
      if (design.elements) {
        await Promise.all(
          design.elements.map(async (element: any) => {
            const transformedElement = await transformElementFromApi(element)
            if (transformedElement) {
              elements[transformedElement.id] = transformedElement
            }
          }),
        )
      }

      // TODO: Emit domain event
      // eventBus.emit(designEvents.designLoaded(design))

      return {
        success: true,
        design,
        elements,
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'loadDesign' },
        extra: { designId: query.designId },
      })
      return {
        success: false,
        error: error instanceof Error ? error.message : errorMessages.failedToLoadDesign(),
      }
    }
  },
})

// Helper function to load image dimensions
async function getImageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      // Convert pixels to mm (96 DPI standard: 1px = 0.264583mm)
      const pixelsToMm = 0.264583
      resolve({
        width: img.naturalWidth * pixelsToMm,
        height: img.naturalHeight * pixelsToMm,
      })
    }
    img.onerror = () => {
      // Fallback to default square dimensions on error
      resolve({ width: 50, height: 50 })
    }
    img.src = src
  })
}

// Helper function to transform API elements to domain elements
async function transformElementFromApi(
  apiElement: any,
): Promise<(CanvasElement & { position: { x: number; y: number }; rotation?: number }) | null> {
  // Parse transform string if present (e.g., "scale(0.5, 0.5)")
  const baseTransform = {
    rotation: apiElement.rotation || 0,
    scaleX: 1,
    scaleY: 1,
    skewX: 0,
    skewY: 0,
  }

  let parsedTransform = baseTransform
  if (apiElement.transform && typeof apiElement.transform === 'string') {
    // Parse scale from transform string: "scale(x, y)"
    const scaleMatch = apiElement.transform.match(/scale\(([-\d.]+),\s*([-\d.]+)\)/)
    if (scaleMatch) {
      parsedTransform = {
        ...baseTransform,
        scaleX: parseFloat(scaleMatch[1]),
        scaleY: parseFloat(scaleMatch[2]),
      }
    }
  }

  // Get actual dimensions for images
  let dimensions = { width: 50, height: 50 } // Default dimensions
  if (apiElement.type === 'image' && apiElement.value) {
    dimensions = await getImageDimensions(apiElement.value)
  }

  const baseElement = {
    id: apiElement.elementId,
    position: {
      x: apiElement.centerPosition?.left || 0,
      y: apiElement.centerPosition?.top || 0,
    },
    rotation: apiElement.rotation || 0,
    dimensions,
    transform: parsedTransform,
    locked: false,
    visible: true,
    layer: 0,
    zIndex: 0,
  }

  if (apiElement.type === 'text') {
    return {
      ...baseElement,
      type: 'text' as const,
      content: apiElement.value || '',
      fontSize: apiElement.fontSize || 16,
      fontFamily: apiElement.fontFamily || 'Arial',
      fontWeight: apiElement.fontWeight ?? 400,
      fontItalic: apiElement.fontItalic ?? false,
      textAlign: 'center' as const,
      color: apiElement.fontColor || '#000000',
    }
  } else if (apiElement.type === 'asset') {
    return {
      ...baseElement,
      type: 'asset' as const,
      src: ensureSvgAssetUrl(apiElement.src || ''), // Ensure SVG for vector quality
      alt: apiElement.alt || '',
      assetId: apiElement.assetId,
      assetType: apiElement.assetType, // Required by backend
      color: apiElement.color,
      // Note: folderName and fileName not included - no longer accepted by backend
    }
  } else if (apiElement.type === 'image') {
    return {
      ...baseElement,
      type: 'image' as const,
      src: apiElement.value || '',
      alt: 'User uploaded image',
      value: apiElement.value,
      borderRadius: apiElement.borderRadius || 0,
      filters: apiElement.filters,
    }
  }

  return null
}
